import { functions } from "@/main";
import { AddressSuggestion, PlacesLookup } from "@/utils/GeocodingService";

/**
 * @module GeocodingRepository
 *
 * Address suggestions, from IRES Cloud rather than from Google directly.
 *
 * The Places web service **sends no CORS header**, so a page cannot call it at all. Measured
 * 2026-08-20: the Geocoding endpoint answers a cross-origin request with
 * `access-control-allow-origin: *`, the Places endpoint with nothing. The alternative is the
 * Maps JavaScript `places` library, which is billed per load and therefore cannot be fetched
 * merely because somebody typed an address.
 *
 * That is why Places used to be available only where a map had already been loaded, which made
 * the same address behave differently in a form and in the picker: only the Places path can
 * establish that an address resolves to one place, and green and the postcode backfill both
 * depend on that. Going through Cloud removes the split, because it is always available.
 */

/** What the `lookupAddressSuggestions` callable answers with. */
interface SuggestionLookupResponse {
  suggestions?: AddressSuggestion[];
  /** Present when Places could not be reached or is not configured on the deployment. */
  failure?: string;
}

/**
 * Asks IRES Cloud which addresses Places thinks a query means.
 *
 * @param query - the address as one string, exactly as it would be geocoded.
 * @param isoCountry - ISO 3166-1 alpha-2, or null to search without a country restriction.
 * @param language - locale for the descriptions shown to the user.
 * @returns the suggestions, or an empty list when Places had nothing or could not be reached.
 *   Never throws: the caller falls back to the Geocoding API alone, which is what it did before
 *   this existed.
 */
export async function lookupAddressSuggestions(
  query: string,
  isoCountry: string | null,
  language?: string
): Promise<AddressSuggestion[]> {
  try {
    const callable = functions.httpsCallable("lookupAddressSuggestions");
    const result = await callable({
      query,
      ...(isoCountry && { country: isoCountry }),
      ...(language && { language }),
    });

    // Doubly wrapped: the callable protocol puts the returned value in `data`, and the function
    // itself answers `{ data: ... }`, as the other geocoding callables do.
    const payload = (result.data as { data?: SuggestionLookupResponse } | undefined)?.data;

    return payload?.suggestions ?? [];
  } catch (error) {
    // Suggestions are an improvement on the lookup, never a precondition for it. A failure here
    // must degrade to the Geocoding API rather than fail the address the user is editing.
    return [];
  }
}

/**
 * A {@link PlacesLookup} backed by IRES Cloud.
 *
 * Always available, unlike the Maps JavaScript implementation it replaces. That is the whole
 * point: a form and the map picker now resolve an address identically, instead of the form
 * getting a weaker answer whenever no map had been opened in the session.
 */
export function createPlacesLookup(language?: string): PlacesLookup {
  return (query: string, isoCountry: string | null) =>
    lookupAddressSuggestions(query, isoCountry, language);
}
